
import React from 'react';
import { ServiceDefinition, IntegrationApp, Language, DataSourceMode } from '../../types';

interface AdminDashboardProps {
  language: Language;
  services: ServiceDefinition[];
  integrations: IntegrationApp[];
  // New props for config control
  currentMode: DataSourceMode;
  onUpdateMode: (mode: DataSourceMode) => void;
}

const SimpleLineChart = ({ data, color }: { data: number[], color: string }) => {
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const points = data.map((val, i) => `${i * (100 / (data.length - 1))},${100 - ((val - min) / range) * 100}`).join(' ');
  
  return (
    <svg viewBox="0 0 100 100" className="w-full h-full overflow-visible" preserveAspectRatio="none">
      <defs>
        <linearGradient id={`grad-admin-${color}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`M0,100 L0,${100 - ((data[0] - min) / range) * 100} ${points.split(' ').map(p => `L${p}`).join(' ')} L100,100 Z`} fill={`url(#grad-admin-${color})`} stroke="none" />
      <polyline points={points} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
};

const AdminDashboard: React.FC<AdminDashboardProps> = ({ 
  language, services, integrations, currentMode, onUpdateMode 
}) => {
  const isRtl = language === 'ar';

  const totalUsers = 12450;
  const activeConnections = 8320;
  const totalIntegrationRequests = integrations.reduce((acc, app) => acc + (app.stats?.requests || 0), 0) + 45000;
  const totalServiceUsage = 152000;
  const chartData = [40, 55, 45, 70, 65, 85, 95, 120, 110, 140, 135, 160];

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      
      {/* System Configuration Panel */}
      <div className="bg-slate-900 text-white p-6 rounded-[24px] shadow-lg flex flex-col md:flex-row items-center justify-between gap-6 border border-slate-700">
         <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-xl shadow-lg"><i className="fas fa-server"></i></div>
            <div>
               <h3 className="text-lg font-black">{isRtl ? 'حالة النظام ومصدر البيانات' : 'System Mode & Data Source'}</h3>
               <p className="text-xs text-slate-400 font-medium">{isRtl ? 'تحكم في مصدر البيانات (محلي/سيرفر/تلقائي)' : 'Control where the app fetches data from.'}</p>
            </div>
         </div>
         
         <div className="flex bg-black/40 p-1 rounded-xl border border-white/10">
            {(['local', 'server', 'auto'] as const).map(mode => (
               <button
                  key={mode}
                  onClick={() => onUpdateMode(mode)}
                  className={`
                     px-6 py-2 rounded-lg text-xs font-black uppercase tracking-wider transition-all
                     ${currentMode === mode 
                        ? 'bg-emerald-500 text-white shadow-lg' 
                        : 'text-slate-400 hover:text-white hover:bg-white/5'}
                  `}
               >
                  {mode}
               </button>
            ))}
         </div>
      </div>

      {/* Top Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-[#1a1a20] p-5 rounded-[24px] border border-slate-200 dark:border-white/5 relative overflow-hidden group">
           <div className="flex justify-between items-start mb-4">
              <div className="w-12 h-12 rounded-2xl bg-indigo-500/10 flex items-center justify-center text-indigo-500 text-xl"><i className="fas fa-users"></i></div>
              <span className="bg-emerald-500/10 text-emerald-500 text-[10px] font-black px-2 py-1 rounded-lg flex items-center gap-1">
                 <i className="fas fa-arrow-up"></i> 12%
              </span>
           </div>
           <h3 className="text-3xl font-black text-slate-800 dark:text-white mb-1">{totalUsers.toLocaleString()}</h3>
           <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{isRtl ? 'إجمالي المستخدمين' : 'Total Users'}</p>
        </div>

        <div className="bg-white dark:bg-[#1a1a20] p-5 rounded-[24px] border border-slate-200 dark:border-white/5 relative overflow-hidden group">
           <div className="flex justify-between items-start mb-4">
              <div className="w-12 h-12 rounded-2xl bg-rose-500/10 flex items-center justify-center text-rose-500 text-xl"><i className="fas fa-cubes"></i></div>
              <span className="bg-emerald-500/10 text-emerald-500 text-[10px] font-black px-2 py-1 rounded-lg flex items-center gap-1">
                 <i className="fas fa-arrow-up"></i> 24%
              </span>
           </div>
           <h3 className="text-3xl font-black text-slate-800 dark:text-white mb-1">{totalServiceUsage.toLocaleString()}</h3>
           <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{isRtl ? 'استهلاك الخدمات الداخلية' : 'Internal Services Usage'}</p>
        </div>

        <div className="bg-white dark:bg-[#1a1a20] p-5 rounded-[24px] border border-slate-200 dark:border-white/5 relative overflow-hidden group">
           <div className="flex justify-between items-start mb-4">
              <div className="w-12 h-12 rounded-2xl bg-blue-500/10 flex items-center justify-center text-blue-500 text-xl"><i className="fas fa-network-wired"></i></div>
              <span className="bg-emerald-500/10 text-emerald-500 text-[10px] font-black px-2 py-1 rounded-lg flex items-center gap-1">
                 <i className="fas fa-arrow-up"></i> 8%
              </span>
           </div>
           <h3 className="text-3xl font-black text-slate-800 dark:text-white mb-1">{totalIntegrationRequests.toLocaleString()}</h3>
           <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{isRtl ? 'طلبات الربط الخارجي' : 'External API Requests'}</p>
        </div>

        <div className="bg-white dark:bg-[#1a1a20] p-5 rounded-[24px] border border-slate-200 dark:border-white/5 relative overflow-hidden group">
           <div className="flex justify-between items-start mb-4">
              <div className="w-12 h-12 rounded-2xl bg-amber-500/10 flex items-center justify-center text-amber-500 text-xl"><i className="fas fa-link"></i></div>
              <span className="bg-emerald-500/10 text-emerald-500 text-[10px] font-black px-2 py-1 rounded-lg flex items-center gap-1">
                 <i className="fas fa-check"></i> High
              </span>
           </div>
           <h3 className="text-3xl font-black text-slate-800 dark:text-white mb-1">{activeConnections.toLocaleString()}</h3>
           <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{isRtl ? 'الحسابات المرتبطة' : 'Linked Accounts'}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
         <div className="lg:col-span-2 bg-white dark:bg-[#1a1a20] p-6 lg:p-8 rounded-[32px] border border-slate-200 dark:border-white/5 shadow-sm">
            <div className="flex justify-between items-center mb-8">
               <div>
                  <h3 className="text-lg font-black text-slate-800 dark:text-white">{isRtl ? 'تحليل النظام' : 'System Analytics'}</h3>
                  <p className="text-xs text-slate-400 font-medium">{isRtl ? 'حركة المرور والطلبات خلال الشهر' : 'Traffic and requests over the month'}</p>
               </div>
               <div className="flex gap-2">
                  <select className="bg-slate-100 dark:bg-white/5 border-none rounded-lg text-xs font-bold text-slate-500 outline-none">
                     <option>Last 30 Days</option>
                     <option>Last 7 Days</option>
                  </select>
               </div>
            </div>
            <div className="h-64 w-full">
               <SimpleLineChart data={chartData} color="#10b981" />
            </div>
         </div>

         <div className="bg-white dark:bg-[#1a1a20] p-6 lg:p-8 rounded-[32px] border border-slate-200 dark:border-white/5 shadow-sm flex flex-col">
            <h3 className="text-lg font-black text-slate-800 dark:text-white mb-6">{isRtl ? 'أفضل الخدمات أداءً' : 'Top Performing Services'}</h3>
            <div className="flex-1 space-y-4">
               {services.slice(0, 5).map((service, i) => (
                  <div key={service.id} className="flex items-center gap-4">
                     <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center text-white shadow-md text-xs`}>
                        <i className={`fas ${service.icon}`}></i>
                     </div>
                     <div className="flex-1">
                        <div className="flex justify-between mb-1">
                           <span className="text-xs font-bold text-slate-700 dark:text-slate-200">{service.title}</span>
                           <span className="text-[10px] font-black text-slate-400">{(95 - i*12)}%</span>
                        </div>
                        <div className="w-full h-1.5 bg-slate-100 dark:bg-white/5 rounded-full overflow-hidden">
                           <div className="h-full bg-slate-800 dark:bg-white rounded-full" style={{ width: `${95 - i*12}%` }}></div>
                        </div>
                     </div>
                  </div>
               ))}
            </div>
         </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
